import {
  crmOpportunityWorkbenchDefaultListParams,
} from "../config";
import type { OpportunityWorkbenchListFilters } from "../types";

/** 归一化商机工作台筛选条件。 */
export function normalizeOpportunityWorkbenchFilters(
  filters: OpportunityWorkbenchListFilters,
) {
  return {
    keyword: normalizeOptionalText(filters.keyword),
    customizationKeyword: normalizeOptionalText(filters.customizationKeyword),
    orgId: filters.orgId,
    orgName: normalizeOptionalText(filters.orgName),
    customerKeyword: normalizeOptionalText(filters.customerKeyword),
    travelMonth: normalizeOptionalText(filters.travelMonth),
    stage: filters.stages?.length ? undefined : normalizeOptionalText(filters.stage),
    stages: normalizeStages(filters.stages),
    status: normalizeOptionalText(filters.status),
    signInfoStatuses: normalizeSignInfoStatusFilters(filters.signInfoStatuses),
    ownerUserId: filters.ownerUserId,
    hasSelectedCustomization: filters.hasSelectedCustomization,
    sortField: filters.sortField ?? crmOpportunityWorkbenchDefaultListParams.sortField,
    sortDirection: filters.sortDirection ?? crmOpportunityWorkbenchDefaultListParams.sortDirection,
  };
}

/**
 * 商机列表时间展示：统一为「9月1日10点」。
 * 仅用于列表短展示，不改变后端原始时间字符串。
 */
export function formatOpportunityListDateTime(value?: string | null): string {
  const normalized = value?.trim();
  if (!normalized) {
    return "";
  }
  const match = normalized.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!match) {
    return normalized;
  }
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  return `${month}月${day}日${hour}点`;
}

/** 归一化可选文本筛选值。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 归一化输赢单状态筛选（多选）。 */
export function normalizeSignInfoStatusFilters(
  values?: string[],
): Array<"unfilled" | "won" | "lost"> | undefined {
  const normalized = Array.from(
    new Set(
      (values ?? [])
        .map((value) => value.trim())
        .filter(
          (value): value is "unfilled" | "won" | "lost" =>
            value === "unfilled" || value === "won" || value === "lost",
        ),
    ),
  );
  return normalized.length ? normalized : undefined;
}

/** @deprecated 兼容旧单值调用；新代码请用 normalizeSignInfoStatusFilters。 */
export function normalizeSignInfoStatusFilter(
  value?: string,
): "unfilled" | "won" | "lost" | undefined {
  return normalizeSignInfoStatusFilters(value ? [value] : undefined)?.[0];
}

/** 归一化当前阶段多选筛选值。 */
function normalizeStages(values?: string[]) {
  const normalized = Array.from(
    new Set(
      (values ?? [])
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
  return normalized.length ? normalized : undefined;
}

/**
 * 历史阶段/终态展示文案。
 * 筛选下拉只暴露线路图 15 节点，列表仍需把旧编码译成中文，避免直接显示 created 等英文。
 */
export const OPPORTUNITY_LEGACY_STAGE_LABELS: Record<string, string> = {
  created: "生成商机",
  profile_completed: "完善资料",
  plan_changed: "更改方案",
  lost: "已输单",
  merged: "已合并",
};

/** 解析商机阶段展示文案：优先元数据正式节点，其次历史兼容标签。 */
export function resolveOpportunityStageLabel(
  stage?: string | null,
  stageOptions?: Array<{ value: string; label: string }>,
) {
  const code = stage?.trim() ?? "";
  if (!code) {
    return "阶段待设置";
  }
  const optionLabel = stageOptions?.find((option) => option.value === code)?.label?.trim();
  if (optionLabel) {
    return optionLabel;
  }
  return OPPORTUNITY_LEGACY_STAGE_LABELS[code] ?? code;
}
